import { OrderHistory, EmailProductData, QuantityInfo } from '../../stores/appOrderHistoryStore';
import { Platform } from '../../types/Platforms';
import { PriceAlert } from '../../services/priceAlerts.ts/priceAlertsService';
import { getProductQuantity, calculateProductTotal, isPricedProductLine } from '../platformUtils';
import { getOrderTotal } from '../../components/orderhistory/vatUtils';
import { filterOrdersBySpecificCategories } from '../../components/orderhistory/utils';
import { getCategoryDisplayName } from '../../components/orderhistory/categoryUtils';
import { calculateUnitPrice } from '../../components/orderhistory/aggregations';
import { aggregateQuantityInfos, formatAggregatedQuantity } from '../quantityUtils';
import { productKey, inRange, ReportRange } from './shared';

/**
 * Pure, React-free computation for the Supplier Negotiation report (R4). One
 * supplier at a time, built to be read out loud in a price negotiation. All € on
 * the ex-VAT basis. Inputs are already scoped to the selected restaurant by the
 * data-loading hooks; period filtering happens here against `ReportRange`.
 *
 * Supplier identity mirrors `getSupplierKey()` (`orderHistoryCalculations.ts:52-58`):
 * group by `platform`, EXCEPT `any_invoice` orders which group by `unknownSupplierName`.
 * Supplier spend mirrors `calculateFullSupplierSpending` (Σ ex-VAT `order.total`,
 * includes shipping/deposits).
 */

/** One row of the supplier overview table (doubles as the picker). */
export interface SupplierOverviewRow {
  /** Stable grouping key (= getSupplierKey semantics). */
  supplierKey: string;
  /** Platform enum value, for the logo. */
  platform: Platform;
  /** Supplier name for any_invoice orders (used for label + logo). */
  unknownSupplierName?: string;
  /** Display label. */
  label: string;
  /** Ex-VAT Σ order.total in the period. */
  spend: number;
  /** Share of total purchasing in the period (0-100). */
  percentOfTotal: number;
  orderCount: number;
  avgOrderValue: number;
  /** Spend % change vs the previous equal-length period. */
  spendChangePercent: number;
}

/** One product row inside a supplier's detail view. */
export interface SupplierProductRow {
  key: string;
  name: string;
  articleId: string;
  category?: string;
  /** Display name for the category, null when no category. */
  categoryDisplay: string | null;
  /** Total quantity in order units (Σ getProductQuantity). */
  totalQuantity: number;
  /** Display-friendly aggregated quantity (kg/L/pcs), undefined when no quantityInfo. */
  formattedQuantity?: string;
  orderCount: number;
  /** Ex-VAT Σ price×qty. */
  spend: number;
  /** Σspend / Σqty (per order-unit). */
  avgUnitPrice: number;
  /** Unit price of the first in-period order. */
  firstPrice: number;
  /** Unit price of the latest in-period order. */
  latestPrice: number;
  /** % change first→latest. */
  priceChangePercent: number;
  /** €/kg-or-L-or-pc display (from the latest order), undefined when no quantityInfo. */
  unitPriceDisplay?: string;
  /** Share of this supplier's product spend (0-100). */
  shareOfSupplierSpend: number;
}

/** A supplier's full detail view. */
export interface SupplierDetail {
  supplierKey: string;
  label: string;
  platform: Platform;
  unknownSupplierName?: string;
  /** Ex-VAT Σ order.total with this supplier. */
  spend: number;
  /** Share of total purchasing in the period (0-100). */
  percentOfTotal: number;
  orderCount: number;
  /** # price-alert increases (restaurantPlatformId match, toDate in period, toPrice>fromPrice). */
  priceIncreaseCount: number;
  /** € impact of price changes (pinned formula — see computeSupplierDetail). */
  priceChangeImpactEur: number;
  products: SupplierProductRow[];
  /** Category options for the detail filter (value = product.category). */
  categoryOptions: Array<{ value: string; label: string }>;
  /** Per-restaurant spend split for this supplier (ex-VAT Σ order.total), sorted by
   *  spend desc. Only populated in "all restaurants" mode (orders carry a
   *  `restaurantId` there); a single restaurant yields a single row, so the
   *  component only renders this when there is more than one. */
  spendByRestaurant: Array<{ restaurantId: string; spend: number; orderCount: number }>;
}

export interface SupplierReportData {
  suppliers: SupplierOverviewRow[];
  comparisonHidden: boolean;
}

/** One product row inside a category's detail view — a supplier-aware
 * `SupplierProductRow` (the same product can be bought from several suppliers, so
 * each row is one product from one supplier). */
export interface CategoryProductRow extends SupplierProductRow {
  /** Grouping key of the supplier this row's product was bought from. */
  supplierKey: string;
  /** Display label for that supplier. */
  supplierLabel: string;
  /** Platform enum value (for the logo). */
  platform: Platform;
  /** Supplier name for any_invoice orders (for label + logo). */
  unknownSupplierName?: string;
}

/** One row of the category overview table (doubles as the picker). */
export interface CategoryOverviewRow {
  /** Raw category key ('' for products without a category). */
  category: string;
  /** Display label ('' → component supplies a translated "Uncategorised"). */
  label: string;
  /** Ex-VAT Σ price×qty across all suppliers in the period. */
  spend: number;
  /** Share of total product purchasing in the period (0-100). */
  percentOfTotal: number;
  productCount: number;
  supplierCount: number;
  /** Spend % change vs the previous equal-length period. */
  spendChangePercent: number;
}

export interface CategoryReportData {
  categories: CategoryOverviewRow[];
  comparisonHidden: boolean;
}

/** A category's full detail view (across suppliers, optionally filtered to one). */
export interface CategoryDetail {
  category: string;
  label: string;
  /** Ex-VAT Σ price×qty for this category in the period (after the supplier filter). */
  spend: number;
  /** Share of total product purchasing in the period (0-100). */
  percentOfTotal: number;
  productCount: number;
  supplierCount: number;
  /** # price-alert increases for this category, toDate in period, price up. */
  priceIncreaseCount: number;
  /** € impact of price changes (same pinned formula as the supplier detail). */
  priceChangeImpactEur: number;
  products: CategoryProductRow[];
  /** Supplier options for the detail filter (value = supplierKey). */
  supplierOptions: Array<{ value: string; label: string }>;
}

// --- supplier identity ----------------------------------------------------

/** Mirrors `getSupplierKey()` (`orderHistoryCalculations.ts:52-58`). */
function supplierKeyOf(order: OrderHistory): string {
  const platform = (order.platform || '').toLowerCase();
  if (platform === 'any_invoice' && order.unknownSupplierName) {
    return order.unknownSupplierName;
  }
  return platform;
}

/** Display label for a supplier: the supplier name for any_invoice, else the platform.
 * Returns '' when nothing is available (practically unreachable; the component then
 * supplies a translated fallback so no English is hardcoded here). */
function supplierLabel(order: OrderHistory): string {
  const platform = (order.platform || '').trim();
  if (platform.toLowerCase() === 'any_invoice' && order.unknownSupplierName) {
    return order.unknownSupplierName.trim();
  }
  return platform || (order.unknownSupplierName || '').trim();
}

// --- overview -------------------------------------------------------------

export interface SupplierReportInputs {
  orders: OrderHistory[];
  range: ReportRange;
  previous: ReportRange | null;
}

export function computeSupplierReport(inputs: SupplierReportInputs): SupplierReportData {
  const { orders, range, previous } = inputs;

  const curOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const prevOrders = previous ? orders.filter((o) => inRange(new Date(o.orderDate), previous)) : [];

  interface Agg {
    supplierKey: string;
    platform: Platform;
    unknownSupplierName?: string;
    label: string;
    spend: number;
    orderCount: number;
  }
  const cur = new Map<string, Agg>();
  curOrders.forEach((order) => {
    const key = supplierKeyOf(order);
    const agg =
      cur.get(key) ||
      ({
        supplierKey: key,
        platform: order.platform,
        unknownSupplierName: order.unknownSupplierName,
        label: supplierLabel(order),
        spend: 0,
        orderCount: 0,
      } as Agg);
    agg.spend += getOrderTotal(order, true);
    agg.orderCount += 1;
    cur.set(key, agg);
  });

  // Previous-period spend per supplier, for the trend.
  const prevSpend = new Map<string, number>();
  prevOrders.forEach((order) => {
    const key = supplierKeyOf(order);
    prevSpend.set(key, (prevSpend.get(key) || 0) + getOrderTotal(order, true));
  });

  const totalSpend = Array.from(cur.values()).reduce((sum, a) => sum + a.spend, 0);

  const suppliers: SupplierOverviewRow[] = Array.from(cur.values())
    .map((agg) => {
      const prev = prevSpend.get(agg.supplierKey) || 0;
      const spendChangePercent =
        prev > 0 ? ((agg.spend - prev) / prev) * 100 : agg.spend > 0 ? 100 : 0;
      return {
        supplierKey: agg.supplierKey,
        platform: agg.platform,
        unknownSupplierName: agg.unknownSupplierName,
        label: agg.label,
        spend: agg.spend,
        percentOfTotal: totalSpend > 0 ? (agg.spend / totalSpend) * 100 : 0,
        orderCount: agg.orderCount,
        avgOrderValue: agg.orderCount > 0 ? agg.spend / agg.orderCount : 0,
        spendChangePercent,
      };
    })
    .sort((a, b) => b.spend - a.spend);

  return { suppliers, comparisonHidden: range.comparisonHidden };
}

// --- detail ---------------------------------------------------------------

export interface SupplierDetailInputs {
  orders: OrderHistory[];
  priceAlerts: PriceAlert[];
  range: ReportRange;
  supplierKey: string;
  /** Selected category filters on `product.category`; empty = no filter. */
  categoryFilters: string[];
}

export function computeSupplierDetail(inputs: SupplierDetailInputs): SupplierDetail | null {
  const { orders, priceAlerts, range, supplierKey, categoryFilters } = inputs;

  const periodOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const supplierOrders = periodOrders.filter((o) => supplierKeyOf(o) === supplierKey);
  if (supplierOrders.length === 0) return null;

  const sample = supplierOrders[0];
  const totalSpendAll = periodOrders.reduce((sum, o) => sum + getOrderTotal(o, true), 0);
  // Header spend uses the Σ order.total convention (includes shipping/deposits).
  const spend = supplierOrders.reduce((sum, o) => sum + getOrderTotal(o, true), 0);

  // Category options come from the supplier's UNFILTERED period orders.
  const categoryMap = new Map<string, string>();
  supplierOrders.forEach((order) => {
    order.productData.forEach((product) => {
      if (product.category && !categoryMap.has(product.category)) {
        categoryMap.set(
          product.category,
          getCategoryDisplayName(product.category, product.mainCategory) || product.category
        );
      }
    });
  });
  const categoryOptions = Array.from(categoryMap.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Apply the optional category filter (same helper as the order-history page).
  const filteredOrders =
    categoryFilters.length === 0
      ? supplierOrders
      : filterOrdersBySpecificCategories(supplierOrders, categoryFilters);

  // Aggregate per product.
  interface ProductAgg {
    key: string;
    name: string;
    articleId: string;
    category?: string;
    mainCategory?: string;
    spend: number;
    quantity: number;
    orderIds: Set<string>;
    lines: Array<{ rawQuantity: number; quantityInfo?: QuantityInfo; articleId: string; platform: string; name?: string }>;
    occurrences: Array<{ date: number; price: number; qty: number }>;
    latest: { date: number; price: number; quantityInfo?: QuantityInfo; category?: string };
  }
  const products = new Map<string, ProductAgg>();

  filteredOrders.forEach((order) => {
    const orderDate = new Date(order.orderDate).getTime();
    order.productData.forEach((product: EmailProductData) => {
      const key = productKey(order.platform, product.articleId, product.name);
      const qty = getProductQuantity(product, order.platform);
      const agg =
        products.get(key) ||
        ({
          key,
          name: product.name,
          articleId: product.articleId,
          category: product.category,
          mainCategory: product.mainCategory,
          spend: 0,
          quantity: 0,
          orderIds: new Set<string>(),
          lines: [],
          occurrences: [],
          latest: { date: -Infinity, price: 0, quantityInfo: undefined, category: product.category },
        } as ProductAgg);
      agg.spend += calculateProductTotal(product, order.platform, true);
      agg.quantity += qty;
      agg.orderIds.add(order.id);
      agg.lines.push({
        rawQuantity: product.rawQuantity,
        quantityInfo: product.quantityInfo,
        articleId: product.articleId,
        platform: order.platform,
        name: product.name,
      });
      // Price trend / first→latest / € impact read PRICED lines only: a one-off free bottle would
      // otherwise show as a -100% "price change" and count its whole spend as impact. Spend, quantity
      // and the aggregated pack size above still include every line.
      if (isPricedProductLine(product)) {
        agg.occurrences.push({ date: orderDate, price: product.price, qty });
        if (orderDate >= agg.latest.date) {
          agg.latest = { date: orderDate, price: product.price, quantityInfo: product.quantityInfo, category: product.category };
        }
      }
      products.set(key, agg);
    });
  });

  const totalProductSpend = Array.from(products.values()).reduce((sum, p) => sum + p.spend, 0);

  // € impact of price changes (pinned formula): per product, sort in-period orders
  // oldest→newest; firstPrice = unit price in the first order; impact = Σ over
  // subsequent orders i of (price_i − firstPrice) × qty_i. Sum across products.
  // Computed over the UNFILTERED supplier orders (like the other header KPIs) so
  // the header stays stable under category filtering.
  let priceChangeImpactEur = 0;
  const occurrencesByProduct = new Map<string, Array<{ date: number; price: number; qty: number }>>();
  supplierOrders.forEach((order) => {
    const orderDate = new Date(order.orderDate).getTime();
    order.productData.forEach((product: EmailProductData) => {
      // Priced lines only — a free line would make firstPrice 0 and bill the product's whole
      // subsequent spend as "price change impact".
      if (!isPricedProductLine(product)) return;
      const key = productKey(order.platform, product.articleId, product.name);
      const list = occurrencesByProduct.get(key) || [];
      list.push({ date: orderDate, price: product.price, qty: getProductQuantity(product, order.platform) });
      occurrencesByProduct.set(key, list);
    });
  });
  occurrencesByProduct.forEach((occurrences) => {
    const sorted = occurrences.slice().sort((a, b) => a.date - b.date);
    const firstPrice = sorted[0].price;
    for (let i = 1; i < sorted.length; i++) {
      priceChangeImpactEur += (sorted[i].price - firstPrice) * sorted[i].qty;
    }
  });

  const productRows: SupplierProductRow[] = Array.from(products.values()).map((agg) => {
    const sorted = agg.occurrences.slice().sort((a, b) => a.date - b.date);
    const firstPrice = sorted.length > 0 ? sorted[0].price : 0;
    const latestPrice = sorted.length > 0 ? sorted[sorted.length - 1].price : 0;
    const aggregated = aggregateQuantityInfos(agg.lines, agg.key);
    const formatted = formatAggregatedQuantity(aggregated.totalRawQuantity, aggregated, agg.category);
    const unitPrice = calculateUnitPrice(agg.latest.price, agg.latest.quantityInfo, agg.latest.category);
    return {
      key: agg.key,
      name: agg.name,
      articleId: agg.articleId,
      category: agg.category,
      categoryDisplay: getCategoryDisplayName(agg.category, agg.mainCategory),
      totalQuantity: agg.quantity,
      formattedQuantity: formatted?.displayText,
      orderCount: agg.orderIds.size,
      spend: agg.spend,
      avgUnitPrice: agg.quantity > 0 ? agg.spend / agg.quantity : 0,
      firstPrice,
      latestPrice,
      priceChangePercent: firstPrice !== 0 ? ((latestPrice - firstPrice) / firstPrice) * 100 : 0,
      unitPriceDisplay: unitPrice?.displayText,
      shareOfSupplierSpend: totalProductSpend > 0 ? (agg.spend / totalProductSpend) * 100 : 0,
    };
  });
  productRows.sort((a, b) => b.spend - a.spend);

  // Per-restaurant spend split (holding view). Orders carry a restaurantId only in
  // all-restaurants mode; otherwise this collapses to one row and the component skips it.
  const spendByRestaurantMap = new Map<string, { spend: number; orderCount: number }>();
  supplierOrders.forEach((order) => {
    const id = order.restaurantId || '';
    if (!id) return;
    const agg = spendByRestaurantMap.get(id) || { spend: 0, orderCount: 0 };
    agg.spend += getOrderTotal(order, true);
    agg.orderCount += 1;
    spendByRestaurantMap.set(id, agg);
  });
  const spendByRestaurant = Array.from(spendByRestaurantMap.entries())
    .map(([restaurantId, v]) => ({ restaurantId, spend: v.spend, orderCount: v.orderCount }))
    .sort((a, b) => b.spend - a.spend);

  // # price increases: alerts for this supplier (by restaurantPlatformId), toDate
  // in the period, where the price went up.
  const restaurantPlatformIds = new Set(supplierOrders.map((o) => o.restaurantPlatformId));
  const priceIncreaseCount = priceAlerts.filter((alert) => {
    if (!restaurantPlatformIds.has(alert.restaurantPlatformId)) return false;
    const toDate = new Date(alert.toDate);
    if (Number.isNaN(toDate.getTime()) || !inRange(toDate, range)) return false;
    return alert.toPrice > alert.fromPrice;
  }).length;

  return {
    supplierKey,
    label: supplierLabel(sample),
    platform: sample.platform,
    unknownSupplierName: sample.unknownSupplierName,
    spend,
    percentOfTotal: totalSpendAll > 0 ? (spend / totalSpendAll) * 100 : 0,
    orderCount: supplierOrders.length,
    priceIncreaseCount,
    priceChangeImpactEur,
    products: productRows,
    categoryOptions,
    spendByRestaurant,
  };
}

// --- category split -------------------------------------------------------

/** Products without a `product.category` group under this key (component shows a
 *  translated "Uncategorised" label). */
const UNCATEGORISED_KEY = '';

function categoryKeyOf(product: EmailProductData): string {
  return product.category || UNCATEGORISED_KEY;
}

export interface CategoryReportInputs {
  orders: OrderHistory[];
  range: ReportRange;
  previous: ReportRange | null;
}

/**
 * Category overview: the same purchasing data as the supplier overview, but
 * grouped by `product.category` across ALL suppliers (e.g. all meat bought, all
 * seafood). Doubles as the picker for the category detail. Spend is the product
 * Σ price×qty convention (categories are a product attribute, so the order-level
 * Σ order.total convention used for suppliers doesn't apply); share is relative
 * to total product spend in the period.
 */
export function computeCategoryReport(inputs: CategoryReportInputs): CategoryReportData {
  const { orders, range, previous } = inputs;

  const curOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const prevOrders = previous ? orders.filter((o) => inRange(new Date(o.orderDate), previous)) : [];

  interface Agg {
    category: string;
    label: string;
    spend: number;
    products: Set<string>;
    suppliers: Set<string>;
  }
  const cur = new Map<string, Agg>();
  let totalSpend = 0;
  curOrders.forEach((order) => {
    const sk = supplierKeyOf(order);
    order.productData.forEach((product: EmailProductData) => {
      const category = categoryKeyOf(product);
      const spend = calculateProductTotal(product, order.platform, true);
      totalSpend += spend;
      const agg =
        cur.get(category) ||
        ({
          category,
          label: getCategoryDisplayName(product.category, product.mainCategory) || '',
          spend: 0,
          products: new Set<string>(),
          suppliers: new Set<string>(),
        } as Agg);
      agg.spend += spend;
      agg.products.add(productKey(order.platform, product.articleId, product.name));
      agg.suppliers.add(sk);
      cur.set(category, agg);
    });
  });

  const prevSpend = new Map<string, number>();
  prevOrders.forEach((order) => {
    order.productData.forEach((product: EmailProductData) => {
      const category = categoryKeyOf(product);
      prevSpend.set(category, (prevSpend.get(category) || 0) + calculateProductTotal(product, order.platform, true));
    });
  });

  const categories: CategoryOverviewRow[] = Array.from(cur.values())
    .map((agg) => {
      const prev = prevSpend.get(agg.category) || 0;
      const spendChangePercent = prev > 0 ? ((agg.spend - prev) / prev) * 100 : agg.spend > 0 ? 100 : 0;
      return {
        category: agg.category,
        label: agg.label,
        spend: agg.spend,
        percentOfTotal: totalSpend > 0 ? (agg.spend / totalSpend) * 100 : 0,
        productCount: agg.products.size,
        supplierCount: agg.suppliers.size,
        spendChangePercent,
      };
    })
    .sort((a, b) => b.spend - a.spend);

  return { categories, comparisonHidden: range.comparisonHidden };
}

export interface CategoryDetailInputs {
  orders: OrderHistory[];
  priceAlerts: PriceAlert[];
  range: ReportRange;
  /** Selected category key ('' = uncategorised). */
  category: string;
  /** Selected supplier filter; 'all' = every supplier. */
  supplierFilter: string;
}

/**
 * Category detail: every product in one category, one row per (supplier, product)
 * so the same SKU bought from two suppliers stays distinct. Optionally filtered to
 * a single supplier. Mirrors the supplier detail's per-product math (qty, spend,
 * first/latest price, €/base-unit, price-change € impact) so the two views are
 * directly comparable.
 */
export function computeCategoryDetail(inputs: CategoryDetailInputs): CategoryDetail | null {
  const { orders, priceAlerts, range, category, supplierFilter } = inputs;

  const periodOrders = orders.filter((o) => inRange(new Date(o.orderDate), range));
  const matchesCategory = (product: EmailProductData) => categoryKeyOf(product) === category;

  // Supplier options span the WHOLE category (independent of the supplier filter).
  const supplierMap = new Map<string, string>();
  periodOrders.forEach((order) => {
    if (!order.productData.some(matchesCategory)) return;
    const sk = supplierKeyOf(order);
    if (!supplierMap.has(sk)) supplierMap.set(sk, supplierLabel(order));
  });
  if (supplierMap.size === 0) return null;
  const supplierOptions = Array.from(supplierMap.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Share-of-purchasing denominator = total product spend across the period.
  let totalProductSpend = 0;
  periodOrders.forEach((order) => {
    order.productData.forEach((product: EmailProductData) => {
      totalProductSpend += calculateProductTotal(product, order.platform, true);
    });
  });

  // Orders contributing to this category (optionally narrowed to one supplier).
  const categoryOrders = periodOrders.filter(
    (o) => o.productData.some(matchesCategory) && (supplierFilter === 'all' || supplierKeyOf(o) === supplierFilter)
  );

  interface ProductAgg {
    rowKey: string;
    productKey: string;
    name: string;
    articleId: string;
    category?: string;
    mainCategory?: string;
    supplierKey: string;
    supplierLabel: string;
    platform: Platform;
    unknownSupplierName?: string;
    spend: number;
    quantity: number;
    orderIds: Set<string>;
    lines: Array<{ rawQuantity: number; quantityInfo?: QuantityInfo; articleId: string; platform: string; name?: string }>;
    occurrences: Array<{ date: number; price: number; qty: number }>;
    latest: { date: number; price: number; quantityInfo?: QuantityInfo; category?: string };
  }
  const products = new Map<string, ProductAgg>();
  const supplierSet = new Set<string>();
  const restaurantPlatformIds = new Set<string>();
  let label = '';
  let spend = 0;

  categoryOrders.forEach((order) => {
    const orderDate = new Date(order.orderDate).getTime();
    const sk = supplierKeyOf(order);
    const sLabel = supplierLabel(order);
    order.productData.forEach((product: EmailProductData) => {
      if (!matchesCategory(product)) return;
      if (!label) label = getCategoryDisplayName(product.category, product.mainCategory) || '';
      supplierSet.add(sk);
      restaurantPlatformIds.add(order.restaurantPlatformId);
      const pk = productKey(order.platform, product.articleId, product.name);
      const rowKey = `${sk}::${pk}`;
      const qty = getProductQuantity(product, order.platform);
      const sp = calculateProductTotal(product, order.platform, true);
      spend += sp;
      const agg =
        products.get(rowKey) ||
        ({
          rowKey,
          productKey: pk,
          name: product.name,
          articleId: product.articleId,
          category: product.category,
          mainCategory: product.mainCategory,
          supplierKey: sk,
          supplierLabel: sLabel,
          platform: order.platform,
          unknownSupplierName: order.unknownSupplierName,
          spend: 0,
          quantity: 0,
          orderIds: new Set<string>(),
          lines: [],
          occurrences: [],
          latest: { date: -Infinity, price: 0, quantityInfo: undefined, category: product.category },
        } as ProductAgg);
      agg.spend += sp;
      agg.quantity += qty;
      agg.orderIds.add(order.id);
      agg.lines.push({
        rawQuantity: product.rawQuantity,
        quantityInfo: product.quantityInfo,
        articleId: product.articleId,
        platform: order.platform,
        name: product.name,
      });
      // Price trend / first→latest / € impact read PRICED lines only: a one-off free bottle would
      // otherwise show as a -100% "price change" and count its whole spend as impact. Spend, quantity
      // and the aggregated pack size above still include every line.
      if (isPricedProductLine(product)) {
        agg.occurrences.push({ date: orderDate, price: product.price, qty });
        if (orderDate >= agg.latest.date) {
          agg.latest = { date: orderDate, price: product.price, quantityInfo: product.quantityInfo, category: product.category };
        }
      }
      products.set(rowKey, agg);
    });
  });

  // € impact of price changes (pinned formula, same as the supplier detail): per
  // product (per supplier) sort in-period orders oldest→newest; impact = Σ over
  // subsequent orders of (price_i − firstPrice) × qty_i.
  let priceChangeImpactEur = 0;
  const productRows: CategoryProductRow[] = Array.from(products.values()).map((agg) => {
    const sorted = agg.occurrences.slice().sort((a, b) => a.date - b.date);
    const firstPrice = sorted.length > 0 ? sorted[0].price : 0;
    const latestPrice = sorted.length > 0 ? sorted[sorted.length - 1].price : 0;
    for (let i = 1; i < sorted.length; i++) {
      priceChangeImpactEur += (sorted[i].price - firstPrice) * sorted[i].qty;
    }
    const aggregated = aggregateQuantityInfos(agg.lines, agg.productKey);
    const formatted = formatAggregatedQuantity(aggregated.totalRawQuantity, aggregated, agg.category);
    const unitPrice = calculateUnitPrice(agg.latest.price, agg.latest.quantityInfo, agg.latest.category);
    return {
      key: agg.rowKey,
      name: agg.name,
      articleId: agg.articleId,
      category: agg.category,
      categoryDisplay: getCategoryDisplayName(agg.category, agg.mainCategory),
      supplierKey: agg.supplierKey,
      supplierLabel: agg.supplierLabel,
      platform: agg.platform,
      unknownSupplierName: agg.unknownSupplierName,
      totalQuantity: agg.quantity,
      formattedQuantity: formatted?.displayText,
      orderCount: agg.orderIds.size,
      spend: agg.spend,
      avgUnitPrice: agg.quantity > 0 ? agg.spend / agg.quantity : 0,
      firstPrice,
      latestPrice,
      priceChangePercent: firstPrice !== 0 ? ((latestPrice - firstPrice) / firstPrice) * 100 : 0,
      unitPriceDisplay: unitPrice?.displayText,
      // "Share of supplier spend" reused as share of CATEGORY spend here.
      shareOfSupplierSpend: spend > 0 ? (agg.spend / spend) * 100 : 0,
    };
  });
  productRows.sort((a, b) => b.spend - a.spend);

  // # price increases: alerts in this category for the suppliers in view.
  const priceIncreaseCount = priceAlerts.filter((alert) => {
    if (!restaurantPlatformIds.has(alert.restaurantPlatformId)) return false;
    if ((alert.category || UNCATEGORISED_KEY) !== category) return false;
    const toDate = new Date(alert.toDate);
    if (Number.isNaN(toDate.getTime()) || !inRange(toDate, range)) return false;
    return alert.toPrice > alert.fromPrice;
  }).length;

  return {
    category,
    label,
    spend,
    percentOfTotal: totalProductSpend > 0 ? (spend / totalProductSpend) * 100 : 0,
    productCount: products.size,
    supplierCount: supplierSet.size,
    priceIncreaseCount,
    priceChangeImpactEur,
    products: productRows,
    supplierOptions,
  };
}
